import React from "react";
import {
    Card,
    CardContent,
} from "@/components/ui/Card"
import { Input } from "@/components/ui/Input"
import { Button } from "@/components/ui/Button"
import LossScenarioChart from "@/components/graph/LossScenarioChart";
import MflBusinessInterruptionChart from "@/components/graph/MflBusinessInterruptionChart";
import RecommendationPriorityChart from "@/components/graph/RecommendationPriorityChart";
import DeleteIcon from "@/components/icons/DeleteIcon";

export const createLossRow = (overrides = {}) => ({
    id: overrides.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    persistedId: overrides.persistedId ?? null,
    risk: overrides.risk ?? "",
    nle: overrides.nle ?? "",
    pml: overrides.pml ?? "",
    mfl: overrides.mfl ?? "",
    mflWeeks: overrides.mflWeeks ?? "",
})

/** Map GET/PUT `loss` payload from API into client state so persistedId is set after save (avoids re-INSERT duplicates). */
export const mapServerLossRowsToState = (lossServerRows) => {
    if (!Array.isArray(lossServerRows) || lossServerRows.length === 0) {
        return [createLossRow()]
    }
    return lossServerRows.map((row) =>
        createLossRow({
            id: `loss-${row.id}`,
            persistedId: row.id,
            risk: row.risk || "",
            nle: row.nle ?? "",
            pml: row.pml ?? "",
            mfl: row.mfl ?? "",
            mflWeeks: row.mfl_weeks ?? "",
        })
    )
}

// Get unique section names from section risk rows
export const getUniqueSectionNamesFromSectionRiskRows = (rows = []) => {
    const seen = new Set()
    const names = []
    for (const row of rows) {
        const name = String(row?.risk ?? "").trim()
        if (!name || seen.has(name)) continue
        seen.add(name)
        names.push(name)
    }
    return names
}

export const createRecommendationRow = (overrides = {}) => ({
    id: overrides.id || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    label: overrides.label ?? "",
    low: overrides.low ?? "",
    medium: overrides.medium ?? "",
    high: overrides.high ?? "",
    mandatory: overrides.mandatory ?? "",
})

// Create initial recommendation rows for the given section names
export const createInitialRecommendationRows = (sectionNames = []) => {
    if (!Array.isArray(sectionNames) || sectionNames.length === 0) return []
    return sectionNames.map((label) => createRecommendationRow({ label }))
}

const isOpenRecommendationLabel = (label) =>
    String(label || "").trim().toLowerCase() === "open recommendations"

const LossAndRecommendationsTab = ({
    lossRows,
    setLossRows,
    recommendationRows,
    setRecommendationRows,
    onLossRowRemoved,
}) => {
    const visibleRecommendationRows = React.useMemo(
        () => (recommendationRows || []).filter((row) => !isOpenRecommendationLabel(row?.label)),
        [recommendationRows]
    )

    const handleLossChange = (rowId, field, value) => {
        setLossRows((prev) =>
            prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
        )
    }

    const handleRiskInput = (rowId, event) => {
        const value = event.target.value
        handleLossChange(rowId, "risk", value)

        // Auto-resize height to fit content
        event.target.style.height = "auto"
        event.target.style.height = `${event.target.scrollHeight}px`
    }

    const addLossRow = () => {
        setLossRows((prev) => [...prev, createLossRow()])
    }

    const removeLossRow = (rowId) => {
        setLossRows((prev) => {
            if (prev.length <= 1) return prev
            const target = prev.find((row) => row.id === rowId)
            const next = prev.filter((row) => row.id !== rowId)
            if (target?.persistedId && onLossRowRemoved) {
                onLossRowRemoved(target.persistedId)
            }
            return next
        })
    }

    const handleRecommendationChange = (rowId, field, value) => {
        setRecommendationRows((prev) =>
            prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row))
        )
    }

    const toNumberOrNull = (value) => {
        if (value === null || value === undefined) return null
        const str = String(value).trim()
        if (!str) return null
        const num = Number(str)
        return Number.isNaN(num) ? null : num
    }

    const getGraphValues = (row) => {
        const nleVal = toNumberOrNull(row.nle)
        const pmlVal = toNumberOrNull(row.pml)
        const mflVal = toNumberOrNull(row.mfl)

        // If no numeric values, show empty cells
        if (nleVal === null && pmlVal === null && mflVal === null) {
            return { nle: "", pml: "", mfl: "" }
        }

        const nleForGraph = nleVal ?? 0

        // PML in the graph is (PML_from_loss - NLE_from_loss), floored at 0
        let pmlForGraphNumber = 0
        let pmlForGraphDisplay = ""
        if (pmlVal !== null) {
            const diff = pmlVal - nleForGraph
            pmlForGraphNumber = Math.max(0, diff)
            pmlForGraphDisplay = pmlForGraphNumber
        }

        // MFL in the graph is: MFL_from_loss - (NLE_graph + PML_graph)
        let mflForGraphDisplay = ""
        if (mflVal !== null) {
            const mflBase = mflVal
            const mflDiff = mflBase - (nleForGraph + pmlForGraphNumber)
            mflForGraphDisplay = Math.max(0, mflDiff)
        }

        return {
            nle: nleVal ?? "",
            pml: pmlForGraphDisplay,
            mfl: mflForGraphDisplay,
        }
    }

    const formatTwoDecimals = (value) => {
        if (value === "" || value === null || value === undefined) return ""
        const num = Number(value)
        if (Number.isNaN(num)) return ""
        return parseFloat(num.toFixed(2))
    }

    return (
        <div className="py-4 space-y-6 slide-up">
            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h5 className="text-lg font-semibold uppercase text-gray-0">Loss Scenarios</h5>
                        <Button
                            type="button"
                            variant="outline"
                            className="p-2 text-sm font-bold bg-red-500 border-gray-500 rounded-md text-gray-0 hover:scale-105"
                            onClick={addLossRow}
                        >
                            add new row
                        </Button>
                    </div>
                    <div className="relative mt-2 overflow-x-auto custom-scrollbar">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-[280px]">
                                        Risk
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        NLE
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        PML
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        MFL
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        MFL Weeks
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-20">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {lossRows.map((row) => (
                                    <tr key={row.id} className="border-b border-gray-600 last:border-b-0">
                                        <td className="px-3 py-2 text-left border border-gray-600 bg-navy-500/40 min-w-[280px] align-top">
                                            <textarea
                                                rows={1}
                                                value={row.risk}
                                                onChange={(e) => handleRiskInput(row.id, e)}
                                                className="w-full px-2 py-1 text-sm bg-transparent border-none rounded outline-none resize-none text-gray-0"
                                                style={{ minHeight: "2.25rem", overflowY: "hidden" }}
                                            />
                                        </td>
                                        {["nle", "pml", "mfl", "mflWeeks"].map((field) => (
                                            <td
                                                key={field}
                                                className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 min-w-40"
                                            >
                                                <Input
                                                    type="number"
                                                    min={1}
                                                    max={100}
                                                    step="0.01"
                                                    value={row[field]}
                                                    onChange={(e) =>
                                                        handleLossChange(row.id, field, e.target.value)
                                                    }
                                                    className="text-sm text-center bg-transparent border-gray-500 h-9 text-gray-0"
                                                />
                                            </td>
                                        ))}
                                        <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 min-w-20">
                                            <div className="flex items-center justify-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => removeLossRow(row.id)}
                                                    disabled={lossRows.length <= 1}
                                                    className="w-full text-xs uppercase transition-transform duration-200 text-gray-0 sm:p-4 hover:text-red-500 sm:h-9 sm:w-auto sm:text-sm hover:scale-110"
                                                >
                                                    <DeleteIcon className="w-5 h-5 text-red-500 transition-transform duration-200 hover:scale-110" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0">Graph Data</h5>
                    <div className="relative mt-2 overflow-x-auto custom-scrollbar">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-[300px]">
                                        Risk
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        NLE
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        PML
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        MFL
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {lossRows.map((row) => {
                                    const { nle, pml, mfl } = getGraphValues(row)
                                    const riskLabel = (row.risk || "").trim()

                                    return (
                                        <tr key={row.id} className="border-b border-gray-600 last:border-b-0">
                                            <td className="px-3 py-2 text-left border border-gray-600 bg-navy-500/40 min-w-[300px] align-top">
                                                <div className="text-sm whitespace-pre-wrap text-gray-0">
                                                    {riskLabel || "-"}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 min-w-40 text-gray-0">
                                                {nle !== "" ? formatTwoDecimals(nle) : "-"}
                                            </td>
                                            <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 min-w-40 text-gray-0">
                                                {pml !== "" ? formatTwoDecimals(pml) : "-"}
                                            </td>
                                            <td className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 min-w-40 text-gray-0">
                                                {mfl !== "" ? formatTwoDecimals(mfl) : "-"}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0">Loss Scenarios (USDM)</h5>
                    <LossScenarioChart rows={lossRows} />
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0">MFL Business Interruption</h5>
                    <MflBusinessInterruptionChart rows={lossRows} />
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0">Recommendations</h5>
                    <div className="relative mt-2 overflow-x-auto custom-scrollbar">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="border-b border-gray-600">
                                    <th className="px-3 py-2 font-semibold text-left uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-[280px]">
                                        Recommendations
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        Low
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        Medium
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        High
                                    </th>
                                    <th className="px-3 py-2 font-semibold text-center uppercase border border-gray-600 bg-navy-500 text-gray-0 min-w-40">
                                        Mandatory
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleRecommendationRows.map((row) => (
                                    <tr key={row.id} className="border-b border-gray-600 last:border-b-0">
                                        <td className="px-3 py-2 text-left border border-gray-600 bg-navy-500/40 text-gray-0 min-w-[280px]">
                                            {row.label}
                                        </td>
                                        {["low", "medium", "high", "mandatory"].map((field) => (
                                            <td
                                                key={field}
                                                className="px-3 py-2 text-center border border-gray-600 bg-navy-500/40 min-w-40"
                                            >
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    max={10}
                                                    step="1"
                                                    value={row[field]}
                                                    onChange={(e) =>
                                                        handleRecommendationChange(row.id, field, e.target.value)
                                                    }
                                                    className="text-sm text-center bg-transparent border-gray-500 h-9 text-gray-0"
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            <Card className="border-none rounded-md shadow-none bg-navy-100">
                <CardContent className="p-6 space-y-4">
                    <h5 className="text-lg font-semibold uppercase text-gray-0"> Open Recommendation Priority</h5>
                    <RecommendationPriorityChart rows={visibleRecommendationRows} />
                </CardContent>
            </Card>
        </div>
    )
}

export default LossAndRecommendationsTab;

